import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router-dom'
import { Toaster } from 'sonner'
import { ConfigProvider } from '@/context/ConfigProvider'
import { SSEProvider } from '@/context/SSEProvider'
import { useUiStore } from '@/stores/uiStore'
import { router } from '@/App'
import { initSentry } from '@/sentry'
import './index.css'

// Initialize Sentry before anything else
initSentry()

function ToastProvider() {
  const theme = useUiStore((s) => s.theme)
  return <Toaster theme={theme} position="bottom-right" richColors aria-live="polite" />
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: 2,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: (failureCount: number, error: any) => {
        if (failureCount >= 1) return false
        // api.ts's normalizeError() flattens every rejection to {message, status, code} —
        // there is no `.response` on the error the mutation actually sees. A genuine
        // network failure (no HTTP response received) normalizes to status: 0.
        if (!error?.status) return true // network error — worth one retry
        return error.status >= 500 // never retry 4xx business errors
      },
    },
  },
})

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element #root not found')

createRoot(rootElement).render(
  <StrictMode>
    <ConfigProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider />
        <SSEProvider>
          <RouterProvider router={router} />
        </SSEProvider>
      </QueryClientProvider>
    </ConfigProvider>
  </StrictMode>,
)
